<template>
  <DeepDialog
    v-model="model"
    card-text-cls="pa-0"
    :loading="isPaymentDataLoading"
    :max-width="500"
    persistent
    title="DeepPay"
  >
    <template #card-title-content-title>
      <DeepAppLogo logo="deeppay" />
    </template>
    <template #activator="activatorProps">
      <slot name="activator" v-bind="activatorProps"></slot>
    </template>
    <template #content>
      <DeepPayPayment
        :box-node-id="boxId"
        :deep-box-node-id="typeId"
        :payment.prop="paymentData"
        @clear="model = false"
        @final="onFinal"
        @logout="onLogout"
      />
    </template>
  </DeepDialog>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { toast } from 'vue-sonner'
import { isBefore } from 'date-fns'
import { DeepAppLogo, DeepDialog, useLoader } from '@deepcloud/deep-ui-lib'
import { useApi } from '@/composables/api'
import DeepPayPayment from '@/components/deeppay/paymentorder/DeepPayPayment.vue'
import type { Node } from '@deepcloud/api-deep-box'

const props = defineProps({
  node: {
    type: Object as PropType<Node>,
    required: true,
  },
  orgId: {
    type: String,
    required: true,
  },
  typeId: {
    type: String,
    required: true,
  },
  boxId: {
    type: String,
    required: true,
  },
  targetNodeInfo: {
    type: Object,
    default: () => ({}),
  },
  originRoute: {
    type: Object,
    default: () => ({}),
  },
})

const model = defineModel({ type: Boolean, default: false })

const { t } = useI18n()
const router = useRouter()
const deepLoader = useLoader()
const { deepOAPI } = useApi()

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function postPaymentsHistory(finalPaymentData: Record<any, any>) {
  if (!props.node?.nodeId) return

  deepOAPI.value.paymentsHistoryDocumentsPayments
    .create(props.node.nodeId, {
      companyId: props.orgId,
      deepBoxId: props.typeId,
      boxId: props.boxId,
      paymentStatus: 'Transmitted',
      paymentData: finalPaymentData,
    })
    .catch(() => {
      toast.error(t('error.error_occurred'))
    })
}

async function onFinal(customEvent: CustomEvent) {
  const finalPaymentData = customEvent.detail?.[0]

  await postPaymentsHistory(finalPaymentData)
}

function goToOriginRoute() {
  if (props.originRoute?.to) {
    router.push(props.originRoute.to)
  }
}

function onLogout() {
  deepLoader.show()
  goToOriginRoute()
  deepLoader.hide()
}

const isPaymentDataLoading = ref(false)

const editorData = ref()

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getField = (field: any): string | undefined => {
  if (!field) return
  const id = field.defaultValue
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return field.probableValues?.find((i: any) => i.id === id)?.value
}

const onGetPaymentData = async (id: string) => {
  try {
    isPaymentDataLoading.value = true
    const res = await deepOAPI.value.editorDocumentTypesInitial.get({
      nodeId: id,
      boxNodeId: props.boxId,
      deepBoxNodeId: props.typeId,
      companyId: props.orgId,
    })
    editorData.value = res.data
  } catch (e) {
    console.warn('Failed to fetch payment data:', e)
    toast.error('Failed to fetch payment data')
  } finally {
    isPaymentDataLoading.value = false
  }
}

const getExecutionDate = () => {
  const dueDate = getField(editorData.value?.documentData?.DueDate)
  const today = new Date()
  const todaysDay = today.getDay()
  const nextWorkingDay = new Date(
    today.setDate(today.getDate() + (todaysDay >= 5 ? 8 - todaysDay : 1)),
  )

  if (dueDate) {
    const executionDate = new Date(dueDate)
    return isBefore(executionDate, today)
      ? nextWorkingDay.toISOString().split('T')[0]
      : dueDate
  }
  return nextWorkingDay.toISOString().split('T')[0]
}

const payersName = computed(() => {
  const data = editorData.value?.documentData
  return (
    getField(data?.CustomerName) ??
    `${getField(data?.CustomerFirstName) ?? ''} ${
      getField(data?.CustomerLastName) ?? ''
    }`.trim()
  )
})

const paymentData = computed(() => {
  const data = editorData.value?.documentData
  if (!data) return { paymentPerAccount: [] }

  const field = (name: keyof typeof data) => getField(data[name]) ?? ''

  return {
    paymentPerAccount: [
      {
        debitIBAN: '',
        possibleIbans: '',
        ownNameAddress: {
          salutation: '',
          firstName: '',
          lastName: '',
          companyName: payersName.value,
          street: { lineWithoutHouseNumber: '', houseNumber: null },
          postalCode: null,
          city: '',
          countryCode: '',
          email: '',
          language: '',
        },
        payments: {
          payment: [
            {
              executionDate: getExecutionDate(),
              currency: field('Currency').toUpperCase(),
              amount: field('TotalInclVat'),
              destinationAccount: {
                iban: {
                  iban: field('PaymentIban').replace(/\s/g, ''),
                  scor: field('PaymentReference').replace(/\s/g, ''),
                  paymentPurpose: field('Notes'),
                },
              },
              otherNameAddress: {
                companyName: field('VendorName'),
                department: '',
                subDepartment: '',
                street: {
                  lineWithoutHouseNumber: field('VendorStreet'),
                  houseNumber: field('VendorHouseNr'),
                },
                buildingName: '',
                floor: '',
                room: '',
                postalCode: field('VendorZip'),
                city: field('VendorCity'),
                townLocation: '',
                district: '',
                countrySubDivision: '',
                countryCode: field('VendorCountry').toUpperCase(),
              },
            },
          ],
        },
      },
    ],
  }
})

const canShowDialog = computed(() => model.value && !!props.node?.nodeId)

watch(
  () => canShowDialog.value,
  (newValue) => {
    if (newValue) {
      onGetPaymentData(props.node?.nodeId)
    }
  },
  { immediate: true },
)
</script>
